import React, { useEffect, useMemo } from 'react';

import { Handle, Position } from '@xyflow/react';

import { TriggerIcon } from 'assets/icons/trigger-icon';
import { eventTypeOptions } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/automated-campaign-slides/event-type-select/event-type-options';
import { EVENT_TYPE_IDS } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/helpers';
import { useWorkflowBuilderContext } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/wizard/builder-context';

import { triggerSchema } from '../../../summary-sidebar/sections/trigger-section';
import { formatDelayString, formatTimeString } from '../../../wizard/helpers';
import { type TriggerNodeData } from '../models';
import { AddNodeHandle } from '../shared/add-node-handle';

import { NodeActions } from './node-actions';
import { NodeRowContainer, StyledBaseNode, StyledDescription, StyledLabel } from './styles';

type TriggerNodeProps = {
  data: TriggerNodeData;
  id: string;
};

export function CustomTriggerNode({ data, id }: TriggerNodeProps) {
  const [builderState] = useWorkflowBuilderContext();
  const { eventTypeId, delayInMinutes, timeOfDayToSend, delayUnitId, selectedNode, recipientSegmentIds } = builderState;
  const selectedEventType = eventTypeOptions.find((t) => t.id === eventTypeId);
  const isConnected = builderState.edges.some((edge) => edge.source === id);

  const isCurrentlySelectedNode = selectedNode?.id === id;
  const { stats } = data;

  let triggerTypeLabel = selectedEventType?.title;
  if (selectedEventType?.id === EVENT_TYPE_IDS.UPCOMING_BIRTHDAY) {
    const timeString = timeOfDayToSend ? ` at ${formatTimeString(timeOfDayToSend)}` : '';
    const delayString = formatDelayString(delayInMinutes, delayUnitId);
    const inAdvanceString = delayString ? ` ${delayString} in advance` : '';
    triggerTypeLabel = `Upcoming birthday - ${inAdvanceString}${timeString}`;
  } else if (selectedEventType?.id === EVENT_TYPE_IDS.ENTERED_SEGMENT) {
    const timeString = timeOfDayToSend ? ` at ${formatTimeString(timeOfDayToSend)}` : '';
    triggerTypeLabel = `Entered segment (${recipientSegmentIds?.length} selected segments) - ${timeString}`;
  }

  const isValid = useMemo(() => triggerSchema.isValidSync(builderState), [builderState]);

  useEffect(() => {
    if (data.reportIsValid) {
      data.reportIsValid(isValid);
    }
  }, [isValid, data]);

  return (
    <StyledBaseNode isError={!isValid} isSelected={isCurrentlySelectedNode}>
      <NodeRowContainer>
        <TriggerIcon />
        <StyledLabel>Trigger</StyledLabel>
      </NodeRowContainer>
      <StyledDescription>{triggerTypeLabel}</StyledDescription>
      <NodeActions
        nodeId={id}
        nodeType={data.nodeType}
        showAnalytics={data.showAnalytics}
        showDeleteButton={false}
        stats={stats}
      />
      {data.onAddNode && (
        <AddNodeHandle isConnected={isConnected} type='next' onAdd={(nodeType) => data.onAddNode!(id, nodeType)} />
      )}
      <Handle position={Position.Bottom} type='source' />
    </StyledBaseNode>
  );
}
